// react
import { useTranslation } from 'react-i18next';

// types
import { Project, StatsFundFlow } from '../../../../@types';

// mui
import { Grid, Typography } from '@mui/material';

// ui
import { IconCircle } from '../../../ui/IconCircle/IconCircle';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';

// links
import { LinkProject } from '../../../links/LinkProject/LinkProject';

// props
export interface TableBasicProjectsFundFlowProps extends Omit<TableProps, 'headers'> {
	data: StatsFundFlow[];
}

const formatAmount = (value: number): string =>
	Number(value).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

export const TableBasicProjectsFundFlow = ({
	data = [],
	...props
}: TableBasicProjectsFundFlowProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const headers: TableHeader[] = [
		{
			isSortable: true,
			label: t('project'),
			render: (_: unknown, __: TableHeader, row: StatsFundFlow) => (
				<Grid alignItems="center" container spacing={1} wrap="nowrap">
					<Grid item>
						<IconCircle
							size="xs"
							TooltipProps={{
								componentsProps: { tooltip: { sx: { maxWidth: 160 } } },
								title: row.project,
							}}
							type="project"
						/>
					</Grid>
					<Grid flex={1} item>
						<LinkProject
							children={row.idWbg}
							project={{ idWbg: row.idWbg, name: row.project } as Project}
							sx={{ whiteSpace: 'nowrap' }}
						/>
					</Grid>
				</Grid>
			),
			value: 'idWbg',
		},
		{
			isSortable: true,
			label: t('name'),
			render: (_: unknown, __: TableHeader, row: StatsFundFlow) => (
				<Typography sx={{ whiteSpace: 'nowrap' }}>{row.project}</Typography>
			),
			value: 'name',
		},
		{
			label: t('currency'),
			render: (_: unknown, __: TableHeader, row: StatsFundFlow) => (
				<Typography>{row.currency ?? '-'}</Typography>
			),
			value: 'currency',
		},
		{
			label: t('inflow'),
			render: (_: unknown, __: TableHeader, row: StatsFundFlow) => (
				<Typography>{formatAmount(row.inflow)}</Typography>
			),
			value: 'inflow',
		},
		{
			label: t('outflow'),
			render: (_: unknown, __: TableHeader, row: StatsFundFlow) => (
				<Typography>{formatAmount(row.outflow)}</Typography>
			),
			value: 'outflow',
		},
		{
			label: t('disbursementPercentage'),
			render: (_: unknown, __: TableHeader, row: StatsFundFlow) => (
				<Typography>{`${Number(row.disbursementPercentage).toFixed(2)}%`}</Typography>
			),
			value: 'disbursementPercentage',
		},
	];

	return <Table color="grey" {...props} data={data} headers={headers} />;
};
